import { useEffect, useState } from "react";
import { CreditCard, X } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";

type Eligibility = { count: number; subscribed: boolean };
const WEEK = 7 * 24 * 60 * 60 * 1000;

export function PaymentMethodNudgeBanner({ warningVisible, warningReady }: { warningVisible: boolean; warningReady: boolean }) {
  const { user, loading, subscription } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [eligible, setEligible] = useState<Eligibility | null>(null);
  const [dismissed, setDismissed] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setEligible(null);
    setDismissed(false);
    if (loading || !user || subscription.is_free || user.user_metadata?.user_type === "client") return;
    let cancelled = false;
    (async () => {
      const [profile, admin, b2b, customers] = await Promise.all([
        supabase.from("caregiver_profiles")
          .select("caregiver_type, is_free_subscription, payment_method_banner_dismissed_at, payment_method_banner_dismiss_count")
          .eq("user_id", user.id).maybeSingle(),
        supabase.rpc("has_role", { _user_id: user.id, _role: "admin" }),
        supabase.rpc("has_role", { _user_id: user.id, _role: "b2b" }),
        supabase.from("stripe_customers").select("has_payment_method")
          .eq("user_id", user.id).order("updated_at", { ascending: false }).limit(1),
      ]);
      if (cancelled || profile.error || admin.error || b2b.error || customers.error) return;
      const p = profile.data;
      if (!p || p.caregiver_type === "bedrijf" || p.is_free_subscription ||
          admin.data || b2b.data ||
          customers.data?.some(({ has_payment_method }) => has_payment_method === true)) return;
      const count = p.payment_method_banner_dismiss_count ?? 0;
      const dismissedAt = p.payment_method_banner_dismissed_at;
      if (count >= 3 || (dismissedAt && Date.now() - new Date(dismissedAt).getTime() < WEEK)) return;
      setEligible({ count, subscribed: subscription.subscribed });
    })();
    return () => { cancelled = true; };
  }, [user, loading, subscription.is_free, subscription.subscribed]);

  const dismiss = async () => {
    if (!user || !eligible || saving) return;
    setSaving(true);
    // An atomic conditional update prevents rapid clicks or concurrent tabs from losing a dismissal.
    const { data, error } = await supabase.from("caregiver_profiles")
      .update({
        payment_method_banner_dismissed_at: new Date().toISOString(),
        payment_method_banner_dismiss_count: eligible.count + 1,
      })
      .eq("user_id", user.id)
      .eq("payment_method_banner_dismiss_count", eligible.count)
      .select("user_id")
      .maybeSingle();
    if (data || (!error && !data)) setDismissed(true);
    setSaving(false);
  };

  if (!eligible || dismissed || !warningReady || warningVisible || location.pathname === "/mijn-praktijk/abonnement") return null;

  return (
    <div className="w-full border-b border-primary/15 bg-primary/5 px-4 py-3">
      <div className="mx-auto flex max-w-5xl flex-wrap items-center gap-x-3 gap-y-2 sm:flex-nowrap">
        <CreditCard className="h-5 w-5 shrink-0 text-primary" aria-hidden="true" />
        <p className="min-w-0 flex-1 text-sm text-foreground">
          {eligible.subscribed
            ? "Koppel je betaalmethode, dan loopt je abonnement vanzelf door en hoef je niet elke keer los te betalen."
            : "Koppel alvast je betaalmethode, dan start je later met één klik je abonnement."}
        </p>
        <Button size="sm" className="h-8 shrink-0 max-sm:ml-8" onClick={() => navigate("/mijn-praktijk/instellingen?betaalmethode=toevoegen")}>
          Betaalmethode toevoegen
        </Button>
        <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0" onClick={dismiss} disabled={saving} aria-label="Betaalmethodebericht sluiten" title="Sluiten">
          <X className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}